import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useOutletContext } from 'react-router-dom'

// #483 — tab-local filter controls belong in the layout's Klasse-filter box, not
// in a separate card of their own. BruktbilLayout exposes the box as a portal
// target; a tab wraps its extra controls in <FilterSlot> to land inside it.
//
// #526 — those controls must not fire the query on change either. The layout
// owns a draft for every filter on screen (global and tab-local) and commits
// the lot on a single "Oppdater", so a tab binds its control to the DRAFT value
// and reads the APPLIED value for its query. See useLocalFilter below.
export interface BruktbilOutletContext {
  filterSlot: HTMLElement | null
  localDraft: Record<string, unknown>
  localApplied: Record<string, unknown>
  setLocalDraft: (key: string, value: unknown) => void
  registerLocalFilter: (key: string, defaultValue: unknown) => void
}

export default function FilterSlot({ children }: { children: ReactNode }) {
  const ctx = useOutletContext<BruktbilOutletContext | null>()
  // Rendered outside the layout (unit tests, standalone) — fall back to inline.
  return ctx?.filterSlot ? createPortal(children, ctx.filterSlot) : <>{children}</>
}

/**
 * A tab-local filter that participates in the layout's single commit point.
 *
 * Returns `[draft, setDraft, applied]`:
 *   - bind the control to `draft` — changing it never fires a request
 *   - put `applied` in the query and its dependency array — it only changes
 *     when the user clicks Oppdater
 *
 * `defaultValue` is what Nullstill restores. Outside the layout (unit tests
 * rendering a tab on its own) the hook degrades to plain local state, so a tab
 * still works standalone.
 */
export function useLocalFilter<T>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void, T] {
  const ctx = useOutletContext<BruktbilOutletContext | null>()
  const register = ctx?.registerLocalFilter

  useEffect(() => {
    register?.(key, defaultValue)
    // defaultValue is a constant per call site; re-registering on every render
    // would reset the user's draft mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, register])

  if (!ctx) return [defaultValue, () => {}, defaultValue]

  const draft = (key in ctx.localDraft ? ctx.localDraft[key] : defaultValue) as T
  const applied = (key in ctx.localApplied ? ctx.localApplied[key] : defaultValue) as T
  return [draft, (value: T) => ctx.setLocalDraft(key, value), applied]
}
